import { Max } from "@pdfking/math"
import { Init } from "@pdfking/reactivity/preact"
import { filter, length, object_map } from "@pdfking/rfunc"
import {
  CreateSingleton,
  Identity,
  Items,
  KeyDrop,
  Keys,
  Partial,
  RecordMap,
  RecordMerge,
  SafePromise,
  Sleep,
  UnixTime,
  Values,
} from "@pdfking/tfunc"
import * as tracker_names from "@pdfking/translations/enum/trackers"
import { RegisterCallbacks } from "../actions/pdfengine/RegisterCallback"
import fire_event from "../utils/fire_event"
import make_translations from "../utils/make_translations"
import render_string from "../utils/render_string"
import uuid from "../utils/uuid"

type Counter = { done: number; total: number }
type Task = {
  error: unknown
  finished: boolean
  start_at: number
  status: Record<string, Record<string, Counter>>
}

export default CreateSingleton(() =>
  Init({
    computed: {
      is_loading() {
        return length(this.trackers) > 0
      },
      trackers(): Record<string, unknown> {
        const blank = () => ({
          done: 0,
          get is_finished() {
            return this.task_total - this.task_done == 0
          },
          task_done: 0,
          task_total: 0,
          total: 0,
        })
        const trackers: Record<
          string,
          {
            aggregated: ReturnType<typeof blank>
            status: Record<string, unknown>
          }
        > = RecordMap(
          (_v, k) => {
            const trans = this.translations.trackers[k]
            return {
              aggregated: blank(),
              status: object_map(
                Identity,
                _v => ({
                  ...blank(),
                  get width(): number {
                    return (
                      (this as ReturnType<typeof blank>).done /
                      trackers[k].aggregated.total
                    )
                  },
                }),
                ["danger", "success", "loading"]
              ),
              get title() {
                return trans ? render_string(trans, this.aggregated) : null
              },
              get width() {
                return 1 / length(trackers)
              },
            }
          },
          tracker_names
        )
        for (const [_task_id, task] of Items(this.tasks)) {
          const key = task.error
            ? "danger"
            : task.finished
              ? "success"
              : "loading"
          for (const [id, counters] of Items(task.status)) {
            for (const { done, total } of Values(counters)) {
              for (const r of [
                trackers[id].status[key] as ReturnType<typeof blank>,
                trackers[id].aggregated,
              ]) {
                r.done += done
                r.total += total
                r.task_done += task.finished ? 1 : 0
                r.task_total += 1
              }
            }
          }
        }
        for (const [id, { aggregated }] of Items(trackers)) {
          if (!aggregated.task_total) {
            delete trackers[id]
          }
        }
        return trackers
      },
      translations: () => make_translations("trackers"),
    },
    data: {
      tasks: {} as Record<string, Task>,
    },
    init() {
      RegisterCallbacks({
        push: (task_id, event) =>
          this.emit(task_id, event.name, task_id, event),
        start: task_id => this.start(task_id),
        stop: (task_id, error) => this.finish(task_id, error),
      })
    },
    methods: {
      cleanup_tracker(_task_id: string) {
        type TrackerEntry = { aggregated: { is_finished: boolean } }
        const to_delete = Keys(
          filter(
            (t: TrackerEntry, _id) => t.aggregated.is_finished,
            this.trackers as Record<string, TrackerEntry>
          )
        )
        let tasks = this.tasks
        for (const [task_id, t] of Items(tasks)) {
          const new_status = KeyDrop(t.status, to_delete)
          if (length(new_status) == 0) {
            tasks = KeyDrop(tasks, [task_id])
          } else {
            tasks = RecordMerge(tasks, {
              [task_id]: RecordMerge(t, { status: new_status }),
            })
          }
        }
        this.tasks = tasks
      },
      emit(
        task_id: string,
        progress_type: string,
        progress_id: string,
        {
          done = null,
          total = null,
        }: { done?: number | null; total?: number | null }
      ) {
        const t = this.start(task_id)
        const type_counters = t.status[progress_type] || {}
        const r = type_counters[progress_id] || { done: 0, total: 0 }
        const new_done = done || r.done || 0
        const new_total = Max(total || r.total || 1, new_done)
        const updated: Task = RecordMerge(t, {
          status: RecordMerge(t.status, {
            [progress_type]: RecordMerge(type_counters, {
              [progress_id]: { done: new_done, total: new_total },
            }),
          }),
        })
        this.tasks = RecordMerge(this.tasks, { [task_id]: updated })

        if (new_done === new_total) this.finish(task_id)
      },
      finish(task_id: string, error?: unknown, timeout: number | null = null) {
        const t = this.start(task_id)
        const status = RecordMap(
          counters =>
            RecordMap(
              v => ({ done: v.total, total: v.total }),
              counters
            ),
          t.status
        )
        this.tasks = RecordMerge(this.tasks, {
          [task_id]: RecordMerge(t, { error, finished: true, status }),
        })
        const delay = error ? 5000 : timeout || 2500
        Sleep(delay).then(Partial(this.cleanup_tracker, task_id))

        error
          ? fire_event(document.documentElement, "tracked_error", error)
          : null
      },
      start(task_id: string): Task {
        if (this.tasks[task_id]) return this.tasks[task_id]
        const task: Task = {
          error: null,
          finished: false,
          start_at: UnixTime(),
          status: {},
        }
        this.tasks = RecordMerge(this.tasks, { [task_id]: task })
        return task
      },
      track_promise(
        promise_factory: (
          emit: (event: { name: string; done?: number; total?: number }) => void
        ) => Promise<unknown> | unknown
      ) {
        const task_id = uuid()
        this.start(task_id)

        const promise = Promise.resolve(
          promise_factory(
            (event: { name: string; done?: number; total?: number }) =>
              this.emit(task_id, event.name, task_id, event)
          )
        )
        SafePromise(promise).then(({ error }) => {
          this.finish(task_id, error)
        })

        return promise
      },
    },
  })
)
